import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { TemplateServerInfo } from "revolt-api";
import { Server } from "revolt.js";
import { Text } from "preact-i18n";
import { useEffect, useRef, useState } from "preact/hooks";

import {
    Category,
    Checkbox,
    Column,
    ComboBox,
    Error,
    InputBox,
    Modal,
} from "@revoltchat/ui";

import { I18nError } from "../../../context/Locale";

import { useClient } from "../../client/ClientController";
import { mapError } from "../../client/jsx/error";
import { ModalProps } from "../types";

type FormData = {
    use_template: boolean;
    template?: string;
    name: string;
};

/**
 * Server creation modal
 */
export default function CreateServer({
    ...props
}: ModalProps<"create_server">) {
    const history = useHistory();
    const client = useClient();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [templates, setTemplates] = useState<
        TemplateServerInfo[] | undefined
    >(undefined);

    const { control, handleSubmit, watch, setValue, errors } =
        useForm<FormData>({
            defaultValues: {
                use_template: false,
                name: "",
                template: "",
            },
        });

    const useTemplate = watch("use_template");

    useEffect(() => {
        client.api.get("/servers/get_template_servers").then((v) => {
            const templates = v as TemplateServerInfo[];
            setTemplates(templates);
        });
    }, [client.api]);

    useEffect(() => {
        if (templates && templates.length > 0) {
            if (useTemplate) {
                setValue("template", templates[0].server_id);
            }
        }
    }, [templates, setValue, watch, useTemplate]);

    const nameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
        }
    }, []);

    const onSubmit: SubmitHandler<FormData> = async ({
        template,
        use_template,
        name,
    }) => {
        try {
            setProcessing(true);
            setError(null);

            let server: Server;

            if (use_template && template) {
                server = await client.servers.createTemplateServer({
                    name,
                    template_id: template,
                });
            } else {
                server = await client.servers.createServer({
                    name,
                });
            }

            history.push(server.path);
            props.onClose();
        } catch (err) {
            setError(mapError(err));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Modal
            {...props}
            disabled={processing}
            title={<Text id="app.main.servers.create" />}
            actions={[
                {
                    onClick: () => void handleSubmit(onSubmit)(),
                    children: <Text id="app.special.modals.actions.create" />,
                    confirmation: true,
                    disabled: processing,
                },
                {
                    onClick: () => props.onClose(),
                    children: <Text id="app.special.modals.actions.cancel" />,
                    palette: "plain",
                },
            ]}>
            <Column>
                <form onSubmit={(e) => e.preventDefault()}>
                    <Controller
                        name="use_template"
                        control={control}
                        render={(field) => (
                            <Checkbox
                                {...field}
                                value={field.value}
                                onChange={(value) => {
                                    field.onChange(value);
                                }}
                                title={
                                    <Text id="app.main.servers.use_template" />
                                }
                            />
                        )}
                    />

                    {useTemplate && (
                        <>
                            <Category>
                                <Text id="app.main.servers.template" />
                            </Category>
                            <Controller
                                name="template"
                                control={control}
                                render={(field) => (
                                    <ComboBox
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e: any) => {
                                            field.onChange(
                                                e.currentTarget.value,
                                            );
                                        }}>
                                        {templates?.map((t) => (
                                            <option
                                                key={t.server_id}
                                                value={t.server_id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </ComboBox>
                                )}
                            />
                        </>
                    )}

                    <Category>
                        <Text id="app.main.servers.name" />
                        <Category compact>
                            <I18nError
                                error={
                                    errors.name?.message &&
                                    `error.${errors.name?.message}`
                                }
                            />
                        </Category>
                    </Category>

                    <Controller
                        name="name"
                        rules={{
                            required: "RequiredField",
                            validate: (value: string) =>
                                value.length === 0
                                    ? "RequiredField"
                                    : value.length > 32
                                      ? "TooLong"
                                      : undefined,
                        }}
                        control={control}
                        render={(field) => (
                            <InputBox {...field} ref={nameInputRef} autoFocus />
                        )}
                    />

                    {error && (
                        <Category>
                            <Error error={error} />
                        </Category>
                    )}
                </form>
            </Column>
        </Modal>
    );
}
